import { Injectable } from '@angular/core';
import {
  AnnouncementType,
  CardColorType,
  SocketMessageType,
} from 'src/app/core/types';
import { SocketIoService } from 'src/app/core/services';
import { GameEventService } from './game-event.service';
import { MatchmakingService } from './matchmaking.service';
import { wait } from 'src/app/core/util';

@Injectable({
  providedIn: 'root',
})
export class GamePlayService {
  private hasLoaded: boolean = false;
  private actionPending = false;
  private lastResyncAt = -Infinity;

  private stateKey = '';
  private consumedState: string | null = null;
  private recoveryTimer?: ReturnType<typeof setTimeout>;
  private resyncPending = false;
  private resyncSawState = false;
  private restorePromise?: Promise<void>;
  private contextEpoch = 0;
  private readonly turnActions = new Set([
    'game/play-card', 'game/give-card', 'game/announce', 'game/decide-kontra',
    'game/shout', 'game/look-talon',
  ]);

  private observeState(event: { name: string; data: any }): void {
    if (event.name === 'waitingFor') {
      const key = JSON.stringify(event.data);
      const changed = key !== this.stateKey;
      this.stateKey = key;
      if (this.resyncPending) {
        // re-enter replays historical turns before the current one. None of
        // those intermediate events may unlock input before its successful ACK.
        this.resyncSawState = true;
      } else if (changed) {
        this.releaseConsumedState();
      }
    } else if (['enteredLobby', 'leftTable'].includes(event.name)) {
      this.contextEpoch++;
      this.actionPending = false;
      this.resyncPending = false;
      this.restorePromise = undefined;
      this.lastResyncAt = -Infinity;
      this.stateKey = '';
      this.releaseConsumedState();
    }
  }

  private releaseConsumedState(): void {
    this.consumedState = null;
    if (this.recoveryTimer) clearTimeout(this.recoveryTimer);
    this.recoveryTimer = undefined;
  }

  private async resync(): Promise<void> {
    if (this.resyncPending || Date.now() - this.lastResyncAt < 5000) return;
    this.lastResyncAt = Date.now();
    try { await this.restoreState(); }
    catch (error) { console.warn('Game state refresh failed:', error); }
  }

  /** Shared by recovery and reconnect: input stays blocked throughout replay. */
  public restoreState(): Promise<void> {
    if (this.restorePromise) return this.restorePromise;
    this.resyncPending = true;
    this.gameEvent.emit({ name: 'gameStateRestoring', data: {} });
    this.resyncSawState = false;
    const epoch = this.contextEpoch;
    const restoring = (async () => {
      try {
        await this.socketIO.post('game/re-enter');
        if (epoch === this.contextEpoch && this.resyncSawState) this.releaseConsumedState();
      } finally {
        if (epoch === this.contextEpoch) {
          this.resyncPending = false;
          this.restorePromise = undefined;
        }
      }
    })();
    this.restorePromise = restoring;
    return restoring;
  }

  private async sendAction(url: string, data?: any): Promise<void> {
    if (this.actionPending || this.resyncPending) return;
    if (this.consumedState !== null) {
      // A later explicit click may retry a read after a failed recovery. Never
      // repeat the mutation while the client still displays the consumed turn.
      void this.resync();
      return;
    }
    this.actionPending = true;
    const epoch = this.contextEpoch;
    const submittedState = this.stateKey;
    const consumesTurn = this.turnActions.has(url);
    if (consumesTurn) this.consumedState = submittedState;
    try {
      await this.socketIO.post(url, data);
    } catch (error) {
      if (epoch !== this.contextEpoch) throw error;
      if (error === 'notWaitingFor') {
        await this.resync();
      } else if (typeof error === 'string' || ((error as any)?.kind === 'CONNECTION' && (error as any)?.requestSent === false)) {
        // Explicit rule rejection or a locally rejected offline request.
        this.releaseConsumedState();
      }
      // A timeout has an unknown result: recover the state without replaying.
      throw error;
    } finally {
      if (epoch === this.contextEpoch) {
        this.actionPending = false;
        if (consumesTurn && this.consumedState !== null && !this.recoveryTimer) {
          this.recoveryTimer = setTimeout(() => { this.recoveryTimer = undefined; void this.resync(); }, 3500);
        }
      }
    }
  }

  constructor(
    private socketIO: SocketIoService,
    private gameEvent: GameEventService,
    private matchmakingService: MatchmakingService
  ) {
    this.gameEvent.gameEvent$.subscribe(event => this.observeState(event));
  }

  public async init() {
    if (this.hasLoaded) return;
    this.hasLoaded = true;
    [
      'gameStart',
      'announcement',
      'finalRoundAnnouncementIs',
      'changeAtoutCard',
      'cardUpdate',
      'announceKontra',
      'cardGiven',
      'announceMarriage',
      'marriagePlayedNoTrick',
      'cardPlayed',
      'atoutUpdate',
      'zuadraht',
      'othersCardUpdate',
      'tookFromStaple',
      'tookFromStapleOther',
      'waitingFor',
      'gameCountUpdate',
      'newRound',
      'rolesUpdate',
      'talonUpdate',
      'roundCountUpdate',
      'pendingCountUpdate',
      'gameEnd',
      'roundEnd',
      'trickEnd',
      'timeoutWarning',
      'timedOut',
      'didAutoAction',
      'userDidAction',
      'newGameStartsSoon',
      'paused',
      'pauseEnded',
      'kickedOut',
    ].forEach((eventType) => {
      this.socketIO.on(eventType, (data) => {
        this.handleEvent(eventType as SocketMessageType, data.data);
      });
    });
  }

  private async handleEvent(event: SocketMessageType, data: any) {
    console.log('Handling event: ', event, data);
    if (event === 'gameEnd') {
      this.gameEvent.emit({ name: 'gameWillEnd', data: data });
      await wait(4000); // add a delay before emitting game end
    }
    this.gameEvent.emit({ name: event, data: data });
  }

  public async playCard(cardNumber: number): Promise<void> {
    try {
      await this.sendAction('game/play-card', { cardNumber: cardNumber });
    } catch (e) {
      console.warn('play card action failed:', e);
    }
  }

  public async giveCard(cardNumber: number): Promise<void> {
    try {
      await this.sendAction('game/give-card', { cardNumber: cardNumber });
    } catch (e) {
      console.warn('give card action failed:', e);
    }
  }

  public async marriage(cardNumber: number): Promise<void> {
    try {
      await this.sendAction('game/marriage', {
        cardNumber: cardNumber,
      });
    } catch (e) {
      console.warn('marriage action failed:', e);
    }
  }

  public async changeAtoutCard() {
    try {
      await this.sendAction('game/change-atout-card');
    } catch (e) {
      console.warn('change atout card action failed:', e);
    }
  }

  public async zuadrahn() {
    try {
      await this.sendAction('game/zuadrahn');
    } catch (e) {
      console.warn('zuadrahn action failed:', e);
    }
  }

  public async lookTalon(
    myCardsToGiveAway: number[],
    cardsIWantToHave: number[]
  ) {
    try {
      await this.sendAction('game/look-talon', {
        myCardsToGiveAway,
        cardsIWantToHave,
      });
    } catch (e) {
      console.warn('look talon action failed:', e);
    }
  }

  public async shout(color: CardColorType | undefined): Promise<void> {
    try {
      await this.sendAction('game/shout', { color: color });
    } catch (e) {
      console.warn('shout action failed:', e);
    }
  }

  public async announce(announcement: AnnouncementType): Promise<void> {
    try {
      await this.sendAction('game/announce', {
        announcement: announcement,
      });
    } catch (e) {
      console.warn('announce action failed:', e);
    }
  }

  public async decideKontra(
    doContra: boolean,
    re: boolean = false
  ): Promise<void> {
    try {
      await this.sendAction('game/decide-kontra', {
        doContra: doContra,
        re: re,
      });
    } catch (e) {
      console.warn('decide kontra action failed:', e);
    }
  }

  public async pause(): Promise<void> {
    try {
      await this.sendAction('game/pause', {});
    } catch (e) {
      console.warn('pause game failed:', e);
    }
  }

  public async endPause(): Promise<void> {
    try {
      await this.sendAction('game/end-pause', {});
    } catch (e) {
      console.warn('end-pause game failed:', e);
    }
  }
}
